import React, { FC } from "react";
import classNames from "classnames";

import { Box, BoxProps } from "components";

import { IconColor, IconName, IconSize } from "./types";
import styles from "./Icon.module.css";

export interface IconProps extends BoxProps {
  color?: IconColor;
  name?: IconName;
  size?: IconSize;
}

export const getIconSize = (size: IconSize) => {
  switch (size) {
    case "xs":
      return "w-2 h-2 md:w-3 md:w-3 xl:w-4 xl:h-4";
    case "sm":
      return "w-3 h-3 md:w-4 md:h-4 xl:w-5 xl:h-5";
    case "md":
      return "w-4 h-4 md:w-5 md:h-5 xl:w-6 xl:h-6";
    case "lg":
      return "w-5 h-5 md:w-6 md:w-6 xl:w-7 xl:h-7";
    case "xl":
      return "w-6 h-6 md:w-7 md:h-7 xl:w-8 xl:h-8";
    case "2xl":
      return "w-7 h-7 md:w-8 md:h-8 xl:w-9 xl:h-9";
    case "3xl":
      return "w-8 h-9 md:w-9 md:h-9 xl:w-10 xl:h-10";
    case "4xl":
      return "w-9 h-9 md:w-10 md:h-10 xl:w-11 xl:h-11";
    case "5xl":
      return "w-10 h-10 md:w-11 md:h-11 xl:w-12 xl:h-12";
    case "6xl":
      return "w-11 h-11 md:w-12 md:h-12 xl:w-13 xl:h-13";
    case "7xl":
      return "w-12 h-12 md:w-13 md:h-13 xl:w-14 xl:h-14";
    case "inherit":
    default:
      return styles.inherit;
  }
};

export const Icon: FC<IconProps> = ({
  children,
  className,
  color,
  name,
  size = "md",
  ...rest
}) => {
  switch (name) {
    case "chevron-down":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      );
    case "chevron-right":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      );
    case "cog":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path
            fill="currentColor"
            d="M444.788 291.1l42.616 24.599c4.867 2.809 7.126 8.618 5.459 13.985-11.07 35.642-29.97 67.842-54.689 94.586a12.016 12.016 0 0 1-14.832 2.254l-42.584-24.595a191.577 191.577 0 0 1-60.759 35.13v49.182a12.01 12.01 0 0 1-9.377 11.718c-34.956 7.85-72.499 8.256-109.219.007-5.49-1.233-9.403-6.096-9.403-11.723v-49.184a191.555 191.555 0 0 1-60.759-35.13l-42.584 24.595a12.016 12.016 0 0 1-14.832-2.254c-24.718-26.744-43.619-58.944-54.689-94.586-1.667-5.366.592-11.175 5.459-13.985L67.212 291.1a193.48 193.48 0 0 1 0-70.199l-42.616-24.599c-4.867-2.809-7.126-8.618-5.459-13.985 11.07-35.642 29.97-67.842 54.689-94.586a12.016 12.016 0 0 1 14.832-2.254l42.584 24.595a191.577 191.577 0 0 1 60.759-35.13V25.759a12.01 12.01 0 0 1 9.377-11.718c34.956-7.85 72.499-8.256 109.219-.007 5.49 1.233 9.403 6.096 9.403 11.723v49.184a191.555 191.555 0 0 1 60.759 35.13l42.584-24.595a12.016 12.016 0 0 1 14.832 2.254c24.718 26.744 43.619 58.944 54.689 94.586 1.667 5.366-.592 11.175-5.459 13.985L444.788 220.9a193.485 193.485 0 0 1 0 70.2zM336 256c0-44.112-35.888-80-80-80s-80 35.888-80 80 35.888 80 80 80 80-35.888 80-80z"
          />
        </svg>
      );
    case "cursor-click":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
          />
        </svg>
      );
    case "email":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path
            fill="currentColor"
            d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"
          />
        </svg>
      );
    case "facebook":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M23.998 12c0-6.628-5.372-12-11.999-12C5.372 0 0 5.372 0 12c0 5.988 4.388 10.952 10.124 11.852v-8.384H7.078v-3.469h3.046V9.356c0-3.008 1.792-4.669 4.532-4.669 1.313 0 2.686.234 2.686.234v2.953H15.83c-1.49 0-1.955.925-1.955 1.874V12h3.328l-.532 3.469h-2.796v8.384c5.736-.9 10.124-5.864 10.124-11.853z"></path>
        </svg>
      );
    case "instagram":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913a5.885 5.885 0 001.384 2.126A5.868 5.868 0 004.14 23.37c.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558a5.898 5.898 0 002.126-1.384 5.86 5.86 0 001.384-2.126c.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913a5.89 5.89 0 00-1.384-2.126A5.847 5.847 0 0019.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227a3.81 3.81 0 01-.899 1.382 3.744 3.744 0 01-1.38.896c-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421a3.716 3.716 0 01-1.379-.899 3.644 3.644 0 01-.9-1.38c-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 01-2.88 0 1.44 1.44 0 012.88 0z"></path>
        </svg>
      );
    case "linkedin":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
        </svg>
      );
    case "location":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512"
        >
          <path
            fill="currentColor"
            d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"
          />
        </svg>
      );
    case "menu":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      );
    case "moon":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      );
    case "phone":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path
            fill="currentColor"
            d="M493.4 24.6l-104-24c-11.3-2.6-22.9 3.3-27.5 13.9l-48 112c-4.2 9.8-1.4 21.3 6.9 28l60.6 49.6c-36 76.7-98.9 140.5-177.2 177.2l-49.6-60.6c-6.8-8.3-18.2-11.1-28-6.9l-112 48C3.9 366.5-2 378.1.6 389.4l24 104C27.1 504.2 36.7 512 48 512c256.1 0 464-207.5 464-464 0-11.2-7.7-20.9-18.6-23.4z"
          />
        </svg>
      );
    case "rip":
      // https://www.flaticon.com/free-icon/grave_2851736?term=rip&page=1&position=30
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          width="512"
          height="512"
          viewBox="0 0 60 60"
        >
          <g fill="none" fillRule="evenodd">
            <g
              fill="currentColor"
              fillRule="nonzero"
              transform="translate(0 -1)"
            >
              <path d="M43.437 6.562A19 19 0 0011 20v29.646a6.984 6.984 0 00-3.408 3.387 7 7 0 00-7.556 5.624c-.11.57.042 1.159.413 1.605.384.468.958.739 1.563.738h55.976c.605 0 1.18-.27 1.563-.738a1.937 1.937 0 00.413-1.605 7 7 0 00-7.556-5.624A6.984 6.984 0 0049 49.646V20a18.915 18.915 0 00-5.563-13.438zM13 20c0-9.389 7.611-17 17-17s17 7.611 17 17v29.068a7.075 7.075 0 00-.948-.068 5.772 5.772 0 00-1.847.282A7.035 7.035 0 0039.029 47a7.746 7.746 0 00-2.484.424A10.045 10.045 0 0030 45a9.853 9.853 0 00-6.537 2.461 7.012 7.012 0 00-7.609 1.8A6.961 6.961 0 0013 49.068zm37.409 33.542a7.1 7.1 0 00-2.087 1.308 1 1 0 101.356 1.47 5.452 5.452 0 012.3-1.2h.015a5.026 5.026 0 016.02 3.869.031.031 0 01-.02.01L2 59.04a5.019 5.019 0 016.012-3.919h.015c.852.21 1.641.62 2.3 1.2a1 1 0 001.356-1.47 7.1 7.1 0 00-2.087-1.308 5 5 0 015.718-2.352 1.958 1.958 0 001.961-.557 5.08 5.08 0 015.48-1.3 2 2 0 002.008-.388 8.048 8.048 0 0110.483 0 2 2 0 002.009.388 5.086 5.086 0 015.48 1.3c.498.54 1.255.754 1.962.557a5 5 0 015.717 2.352z"></path>
              <path d="M32.4 51.8a1 1 0 101.2-1.6 6.09 6.09 0 00-7.2 0 1 1 0 001.2 1.6 4.062 4.062 0 014.8 0zM17.33 53.257a1 1 0 001.34 1.486 2.664 2.664 0 013.661 0 1 1 0 101.338-1.485 4.688 4.688 0 00-6.339-.001zM36.33 53.257a1 1 0 001.34 1.486 2.664 2.664 0 013.661 0 1 1 0 101.338-1.485 4.688 4.688 0 00-6.339-.001zM27.33 55.257a1 1 0 101.34 1.486 2.664 2.664 0 013.661 0 1 1 0 101.338-1.485 4.688 4.688 0 00-6.339-.001zM23.257 34.206l-2.7 1.484a3.51 3.51 0 00-5.417 1.866 3.45 3.45 0 00.292 2.65c.248.459.599.854 1.025 1.155a3.5 3.5 0 106.983-.407l2.636-1.443A2 2 0 0028 41h4a2 2 0 001.926-1.489l2.636 1.443a3.5 3.5 0 006.568 1.88 3.46 3.46 0 00.415-1.473 3.359 3.359 0 001.02-1.147 3.491 3.491 0 00-1.378-4.748 3.5 3.5 0 00-3.742.224l-2.7-1.484a3.507 3.507 0 00-.27-.546 7.021 7.021 0 00.511-2.335l2.462-1.355a3.517 3.517 0 003.733.228 3.49 3.49 0 001.391-4.744 3.38 3.38 0 00-1.026-1.161 3.5 3.5 0 10-6.981.414l-1.931 1.059a6.958 6.958 0 00-9.264 0l-1.93-1.059a3.5 3.5 0 10-6.983-.414c-.424.3-.773.695-1.02 1.153A3.489 3.489 0 0016.82 30.2a3.519 3.519 0 003.734-.227l2.462 1.355c.036.802.208 1.592.511 2.335a3.507 3.507 0 00-.27.543zm-1.407 5.336a1 1 0 00-.474 1.178 1.491 1.491 0 01-.7 1.752 1.513 1.513 0 01-2.042-.587 1.456 1.456 0 01-.161-.933 1 1 0 00-.579-1.065 1.421 1.421 0 01-.705-.641 1.467 1.467 0 01-.126-1.132 1.506 1.506 0 012.587-.559 1 1 0 001.246.232l2.278-1.252a3.5 3.5 0 001.118 1.673zm18.5-1.987a1.506 1.506 0 012.587.559c.112.382.066.793-.13 1.14a1.417 1.417 0 01-.7.633 1 1 0 00-.579 1.065c.052.316-.003.641-.156.923a1.515 1.515 0 01-2.045.6 1.492 1.492 0 01-.706-1.753 1 1 0 00-.474-1.178l-2.438-1.334a3.5 3.5 0 001.118-1.673l2.278 1.252a1 1 0 001.249-.234zm-2.2-11.438a1 1 0 00.474-1.177 1.5 1.5 0 012.748-1.166c.156.285.213.613.161.934a1 1 0 00.595 1.071c.297.125.54.35.689.636a1.49 1.49 0 01-.594 2.027 1.5 1.5 0 01-1.867-.337 1 1 0 00-1.247-.231l-2.358 1.3a6.982 6.982 0 00-.8-1.846zM30 26a5.006 5.006 0 015 5 4.941 4.941 0 01-.574 2.321 1 1 0 00.159 1.152 1.5 1.5 0 01-1.387 2.5 1 1 0 00-1.2.981V39H28v-1.05a1 1 0 00-1.2-.981 1.494 1.494 0 01-1.387-2.5 1 1 0 00.159-1.152A4.941 4.941 0 0125 31a5.006 5.006 0 015-5zm-10.354 2.105a1.5 1.5 0 01-1.868.337 1.492 1.492 0 01-.589-2.036 1.38 1.38 0 01.685-.627 1 1 0 00.6-1.071 1.447 1.447 0 01.156-.924c.192-.35.516-.611.9-.724a1.5 1.5 0 011.848 1.88 1 1 0 00.474 1.177l2.2 1.208a6.982 6.982 0 00-.8 1.846l-2.358-1.3a1 1 0 00-1.248.234z"></path>
              <circle cx="28" cy="31" r="1"></circle>
              <circle cx="32" cy="31" r="1"></circle>
              <path d="M28.293 35.707a1 1 0 001.414 0l.293-.293.293.293a1 1 0 001.414-1.414l-1-1a1 1 0 00-1.414 0l-1 1a1 1 0 000 1.414zM21 19a1 1 0 001-1v-3h.382l1.723 3.447a1 1 0 101.79-.894l-1.466-2.932A2.989 2.989 0 0023 9h-1a2 2 0 00-2 2v7a1 1 0 001 1zm1-8h1a1 1 0 010 2h-1zM35 19a1 1 0 001-1v-3h1a3 3 0 000-6h-1a2 2 0 00-2 2v7a1 1 0 001 1zm1-8h1a1 1 0 010 2h-1zM30 19a1 1 0 001-1v-8a1 1 0 00-2 0v8a1 1 0 001 1z"></path>
            </g>
          </g>
        </svg>
      );
    case "sun":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      );
    case "twitter":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
        >
          <path
            fill="currentColor"
            d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"
          />
        </svg>
      );
    case "x":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    case "yelp":
      return (
        <svg
          className={classNames(color, getIconSize(size), className)}
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M21.111 18.226c-.141.969-2.119 3.483-3.029 3.847-.311.124-.611.094-.85-.09-.154-.12-.314-.365-2.447-3.827l-.633-1.032c-.244-.37-.199-.857.104-1.229.297-.359.732-.494 1.111-.35.02.012 1.596.531 1.596.531 3.588 1.179 3.705 1.224 3.857 1.338.227.186.332.475.285.813h.006zm-7.191-5.267c-.254-.386-.25-.841.012-1.155l.998-1.359c2.189-2.984 2.311-3.141 2.459-3.245.256-.171.57-.179.871-.032.869.422 2.623 3.029 2.729 4.029v.034c.029.341-.105.618-.346.784-.164.105-.314.166-4.393 1.156-.645.164-1.004.254-1.215.329l.029-.03c-.404.12-.854-.074-1.109-.479l-.035-.032zm-2.504-1.546c-.195.061-.789.245-1.519-.938 0 0-4.931-7.759-5.047-7.998-.07-.27.015-.574.255-.82.734-.761 4.717-1.875 5.76-1.621.34.088.574.301.656.604.06.335.545 7.536.615 9.149.066 1.38-.525 1.565-.72 1.624zm.651 7.893c-.011 3.774-.019 3.9-.081 4.079-.105.281-.346.469-.681.53-.96.164-3.967-.946-4.594-1.69a1.002 1.002 0 01-.21-.493c-.016-.12 0-.24.045-.346.075-.195.18-.345 2.88-3.51l.794-.944c.271-.345.75-.45 1.199-.271.436.165.706.54.676.945v1.68l-.028.02zm-8.183-2.414c-.295-.01-.56-.187-.715-.48-.111-.215-.189-.57-.238-1.002-.137-1.301.029-3.264.419-3.887.183-.285.45-.436.745-.426.195 0 .369.061 4.229 1.65l1.13.449c.404.15.654.57.63 1.051-.03.465-.298.824-.694.93l-1.605.51c-3.59 1.155-3.709 1.185-3.898 1.17l-.003.035zm14.977 7.105h-.004l-.005.003.009-.003z"></path>
        </svg>
      );
    default:
      return (
        <Box
          as="i"
          {...(rest as BoxProps)}
          className={classNames(color, getIconSize(size), className)}
        >
          {children}
        </Box>
      );
  }
};
